class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById(
      'ProductCountDesktop',
    );
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document
      .getElementById('ProductGridContainer')
      .querySelector('.collection')
      .classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function')
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function')
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`,
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest('.js-filter'),
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID),
        );

        const newElementSelector = document
          .getElementById(closestJSFilterID)
          .classList.contains('mobile-facets__details')
          ? `#${closestJSFilterID} .mobile-facets__close-button`
          : `#${closestJSFilterID} .facets__summary`;
        const newElementToActivate = document.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    document
      .getElementById('FacetFiltersFormMobile')
      .closest('menu-drawer')
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector('show-more-button .label-show-more.hidden'),
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace('hidden', 'show-more-item'),
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      'facet-filters-form form',
    );
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(
        event.target.closest('form'),
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetSortDrawerForm'
          ) {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this)),
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class IntegerRange extends HTMLElement {
  constructor() {
    super();
    this.filterParam = this.dataset.filterParam;
    this.unit = this.dataset.unit || '';
    this.checkboxes = [];
    this.init();
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    if (!this.filterParam) {
      console.error('❌ Missing data-filter-param on integer-range');
      return;
    }

    // Find input fields
    this.fromInput = this.querySelector('.integer-from');
    this.toInput = this.querySelector('.integer-to');

    if (!this.fromInput || !this.toInput) {
      console.error('❌ Missing .integer-from or .integer-to inputs');
      return;
    }

    // Find checkboxes for this filter
    this.findCheckboxes();

    this.restoreUserInput();

    // Setup event listeners
    this.setupEventListeners();

    // Update status based on current state
    this.updateStatusMessage();
  }

  createStatusElement() {
    // Check if status element already exists
    if (this.querySelector('.integer-range-status')) return;

    // Create status element
    this.statusElement = document.createElement('div');
    this.statusElement.className = 'integer-range-status';
    this.statusElement.style.cssText = `
      margin-top: 8px;
      padding: 8px 12px;
      background: #f9f9f9;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      font-size: 12px;
      color: #666;
      text-align: center;
      line-height: 1.3;
      min-height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Insert after the inputs
    this.appendChild(this.statusElement);
  }



  updateStatusMessage() {
          const activeCheckboxes = this.checkboxes.filter((cb) => cb.checked);
          const userFrom = parseInt(this.fromInput.value.trim());
          const userTo = parseInt(this.toInput.value.trim());
          
          let shouldShowStatus = false;
          let message = '';
          let backgroundColor = '#f9f9f9';
          let textColor = '#666';
          let borderColor = '#e0e0e0';
          
          // hvis min er større enn max
          if (userFrom && userTo && userFrom > userTo) {
            shouldShowStatus = true;
            message = `Ugyldig intervall (prøv et bredere søk)`;
            backgroundColor = '#f8d7da';
            textColor = '#721c24';
            borderColor = '#f1b0b8';
          }
          // sjekker produkter i intervallet
          else if (userFrom && userTo && activeCheckboxes.length > 0) {
            const values = activeCheckboxes.map((cb) => parseInt(cb.value));
            const hasProductsInRange = values.some((v) => v >= userFrom && v <= userTo);
            
            if (!hasProductsInRange) {
              shouldShowStatus = true;
              message = `Ingen produkter i intervallet - prøv et bredere søk`;
              backgroundColor = '#fff3cd';
              textColor = '#856404';
              borderColor = '#ffc107';
            }
          }
          
          // Vis eller skjul status element
          if (shouldShowStatus) {
            this.createStatusElement();
            this.statusElement.textContent = message;
            this.statusElement.style.backgroundColor = backgroundColor;
            this.statusElement.style.color = textColor;
            this.statusElement.style.borderColor = borderColor;
            this.statusElement.style.display = 'flex';
          } else {
            // Skjul status element hvis det eksisterer
            if (this.statusElement) {
              this.statusElement.style.display = 'none';
            }
          }
      }

  restoreUserInput() {
    const urlParams = new URLSearchParams(window.location.search);

    const min = urlParams.get(`integer-range-${this.filterParam}-min`);
    if (min) {
      this.fromInput.value = min;
    }
    const max = urlParams.get(`integer-range-${this.filterParam}-max`);
    if (max) {
      this.toInput.value = max;
    }
  }

 
  findCheckboxes() {
    // Find ALL checkboxes (including hidden ones) to avoid duplicates
    const allCheckboxes = Array.from(
      document.querySelectorAll(
        `input[name="${this.filterParam}"]:not([disabled])`,
      ),
    );

    this.checkboxes = allCheckboxes;
  }

  setupEventListeners() {
    // REAL delay - clear previous timer on each input
    this.fromInput.addEventListener('input', (e) => {
      e.stopPropagation();
      const value = e.target.value;

      // Clear existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Start new timer
      this.debounceTimer = setTimeout(() => {
        this.processFilter();
      }, 1500); // 1.5 seconds delay
    });

    this.toInput.addEventListener('input', (e) => {
      e.stopPropagation();
      const value = e.target.value;

      // Clear existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Start new timer
      this.debounceTimer = setTimeout(() => {
        this.processFilter();
      }, 1500);
    });

    // Enter key for immediate processing
    [this.fromInput, this.toInput].forEach((input) => {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          this.clearTimerAndProcess();
        }
      });
    });
  }

  clearTimerAndProcess() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.processFilter();
  }

  processFilter() {
    const fromVal = this.fromInput.value.trim();
    const toVal = this.toInput.value.trim();

    // Require BOTH fields to be filled
    if ((fromVal === '' && toVal !== '') || (fromVal !== '' && toVal === '')) {
      return;
    }

    // Skip if both fields are empty
    if (fromVal === '' && toVal === '') {
      this.resetToDefault();
      return;
    }

    const fromValue = parseInt(fromVal);
    const toValue = parseInt(toVal);

    // Validate numbers
    if (isNaN(fromValue) || isNaN(toValue)) {
      return;
    }

    // Check for matches BEFORE updating checkboxes
    const potentialMatches = this.checkForMatches(fromValue, toValue);

    this.updateCheckboxes(fromValue, toValue);
  }

  checkForMatches(minValue, maxValue) {
    const matches = [];

    this.checkboxes.forEach((checkbox) => {
      const checkboxValue = parseInt(checkbox.value);
      if (checkboxValue >= minValue && checkboxValue <= maxValue) {
        matches.push(checkboxValue);
      }
    });

    return matches;
  }

  resetToDefault() {
    // unchecker alle checkboxer
    let hasChanges = false;
    this.checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        hasChanges = true;
        checkbox.checked = false;
      }
    });

    if (hasChanges) {
      this.submitForm();
    }

    // Update status message
    this.updateStatusMessage();
  }

  submitForm() {
    // Find Shopify's facet form
    const facetForm = this.closest('form');
    if (!facetForm) {
      console.error('Could not find facet form');
      return;
    }

    // Find facet-filters-form element
    const facetFiltersForm = facetForm.closest('facet-filters-form');
    if (facetFiltersForm && facetFiltersForm.onSubmitHandler) {
      // Create fake event for Shopify's handler
      const fakeEvent = {
        preventDefault: () => {},
        target: facetForm,
        srcElement: { className: '' },
      };

      facetFiltersForm.onSubmitHandler(fakeEvent);
    } else {
      // Fallback: submit form directly
      facetForm.submit();
    }
  }

  updateCheckboxes(minValue, maxValue) {
    let hasChanges = false;

    // Update ALL checkboxes (both check and uncheck)
    this.checkboxes.forEach((checkbox) => {
      const checkboxValue = parseInt(checkbox.value);
      const shouldBeChecked =
        checkboxValue >= minValue && checkboxValue <= maxValue;

      // Check if state changes
      if (checkbox.checked !== shouldBeChecked) {
        hasChanges = true;
      }

      // Update checkbox state
      checkbox.checked = shouldBeChecked;
    });

    // Update status message FIRST (before form submission)
    this.updateStatusMessage();

    // Trigger form submission for server-side filtering
    if (hasChanges) {
      setTimeout(() => {
        this.submitShopifyForm();
      }, 100); // Small delay to let checkboxes update first
    } else {
      console.log(' No changes detected - skipping form submission');
    }
  }

  submitShopifyForm() {
    // Find Shopify's facet form
    const facetForm = this.closest('form');
    if (!facetForm) {
      console.error(' Could not find facet form');
      return;
    }

    // Find facet-filters-form element (Shopify's custom element)
    const facetFiltersForm = facetForm.closest('facet-filters-form');
    if (facetFiltersForm) {
      // Use Shopify's own submission handler
      if (facetFiltersForm.onSubmitHandler) {
        // Create fake event for Shopify's handler
        const fakeEvent = {
          preventDefault: () => {},
          stopPropagation: () => {},
          target: facetForm,
          srcElement: { className: '' },
        };
        facetFiltersForm.onSubmitHandler(fakeEvent);
      } else {
        this.directFormSubmit(facetForm);
      }
    } else {
      console.log(' facet-filters-form not found - trying direct submit');
      this.directFormSubmit(facetForm);
    }
  }

  directFormSubmit(form) {
    // Create FormData from form
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    // Update URL and reload
    const newURL = `${window.location.pathname}?${searchParams.toString()}`;
    window.location.href = newURL;
  }

  getFilterDisplayName() {
    // Get user-friendly name based on parameter
    if (this.filterParam.includes('width')) return 'Bredde';
    if (this.filterParam.includes('height')) return 'Høyde';
    if (this.filterParam.includes('depth')) return 'Dybde';
    return 'verdi'; // fallback
  }

 

}

// Register custom element
customElements.define('integer-range', IntegerRange);
